import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import routes from "../routes/route";
import AuthContext from "../../Auth/AuthContext";
import { toast } from "react-toastify";

/* ✅ Helpers: put ABOVE component */
const isInAppWebView = () => {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get("source") === "app") return true;

  const ua = navigator.userAgent || "";
  const isAndroidWebView =
    /\bwv\b/.test(ua) ||
    /Android.*Version\/[\d.]+.*Chrome\/[\d.]+ Mobile/.test(ua);
  const isIOSWebView = /iPhone|iPad|iPod/.test(ua) && !/Safari/.test(ua);

  return isAndroidWebView || isIOSWebView;
};

// ✅ Detect if app is running as installed PWA
const isRunningAsPWA = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true
  );
};

const DownloadAppPopup = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [canInstall, setCanInstall] = useState(!!window.deferredPrompt);

  // Swipe and Gesture states
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchMoveY, setTouchMoveY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isClosing, setIsClosing] = useState(false);



  useEffect(() => {
    if (window.innerWidth >= 992) return;
    if (isInAppWebView()) return;
    if (isRunningAsPWA()) return;
    if (location.pathname !== "/") {
      setShowPopup(false);
      return;
    }

    if (sessionStorage.getItem("pwa_banner_dismissed") === "true") return;

    const isInstalled = localStorage.getItem("pwaInstalled") === "true";
    if (isInstalled) return;

    setIsClosing(false); // PLEASE KEEP THIS! It fixes the invisible popup bug!
    setShowPopup(true);
  }, [location.pathname]);

  // Auto-close logic: Close after 5 seconds
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  useEffect(() => {
    const handlePromptReady = () => setCanInstall(true);
    const handleShowManually = () => setShowPopup(true);

    window.addEventListener("deferredPromptReady", handlePromptReady);
    window.addEventListener("showPWAInstallPopup", handleShowManually);

    if (window.deferredPrompt) {
      setCanInstall(true);
    }

    return () => {
      window.removeEventListener("deferredPromptReady", handlePromptReady);
      window.removeEventListener("showPWAInstallPopup", handleShowManually);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!window.deferredPrompt) {
      // toast.info("Preparing installer... Please wait or use our menu to install.");
      setShowPopup(false);
      return;
    }

    try {
      window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      console.log("PWA install outcome:", outcome);
      if (outcome === "accepted") {
        localStorage.setItem("pwaInstalled", "true");
        handleClose();
      }
      window.deferredPrompt = null;
      setCanInstall(false);
    } catch (err) {
      console.error("PWA Prompt Error:", err);
      toast.error("An error occurred during installation.");
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsClosing(true);
    sessionStorage.setItem("pwa_banner_dismissed", "true");

    // Wait for animation before unmounting
    setTimeout(() => {
      setShowPopup(false);
    }, 500);
  };

  // ✅ Swipe Handlers
  const handleTouchStart = (e) => {
    if (isClosing) return;
    setTouchStartY(e.touches[0].clientY);
    setTouchMoveY(e.touches[0].clientY);
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (isClosing) return;
    setTouchMoveY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (isClosing) return;
    const swipeDistance = touchStartY - touchMoveY;
    if (swipeDistance > 50) { // Swipe up
      handleClose();
    }
    setIsSwiping(false);
    setTouchStartY(0);
    setTouchMoveY(0);
  };

  if (!showPopup) return null;

  const swipeOffset = isSwiping ? Math.min(0, touchMoveY - touchStartY) : 0;

  return (
    <div
      className="pwa-download-popup"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "fixed",
        top: "12px", // Just below the typical header height
        left: "12px",
        right: "12px",
        background: "rgba(255, 255, 255, 0.98)",
        backdropFilter: "blur(12px)",
        padding: "12px 16px",
        borderRadius: "20px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        zIndex: 999999,
        animation: "slideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        border: "1px solid rgba(0,0,0,0.05)",
        transform: isClosing ? "translateY(-150%)" : `translateY(${swipeOffset}px)`,
        opacity: isClosing ? 0 : 1,
        transition: isSwiping ? "none" : "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease",
        cursor: "grab",
      }}
    >
      <style>
        {`
          @keyframes slideDown { 
            from { transform: translateY(-120%); opacity: 0; } 
            to { transform: translateY(0); opacity: 1; } 
          }
          .pwa-install-btn {
           
            color: #2e85f0;
            border: none;
            padding: 8px 20px;
            border-radius: 16px;
            font-weight: 700;
            font-size: 14px;
            background:transparent;
          
            transition: all 0.2s;
          }
          .pwa-install-btn:active { transform: scale(0.95); }
          
          @media (min-width: 992px) {
            .pwa-download-popup {
              display: none !important;
            }
          }
        `}
      </style>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img
          src="assets/img/fav.png"
          alt="App Icon"
          style={{ width: "42px", height: "42px", borderRadius: "10px", objectFit: "cover" }}
        />
        <div>
          <div style={{ color: "#111", fontWeight: "700", fontSize: "15px", lineHeight: "1.2" }}>
            Install 1860bet
          </div>
          <div style={{ color: "#666", fontSize: "12px" }}>
            1860bet.com
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button className="pwa-install-btn" onClick={handleInstallClick}>
          Install
        </button>
      </div>
    </div>
  );
};

export default DownloadAppPopup;